import {Injectable} from '@angular/core'
import {HttpClient, HttpParams, HttpErrorResponse} from '@angular/common/http'

import {Observable, of} from 'rxjs'
import {catchError, map, tap} from 'rxjs/operators'

import {Domain} from './domain.interface'
import {
  ContractType,
  TerminalType,
  Template,
  DataMap,
  User,
  Asset,
} from '@app/core/services/rest-api.model'
import {
  ApiSearchRequest,
  ApiSearchResponse,
} from '@app/core/services/api.interface'
import {LoggerService} from '@app/core/services/logger.service'
import {DomainSimple} from './domainsimple.interface'

export {Domain, TerminalType, ContractType, DataMap}

export interface GetDomainsRequest extends ApiSearchRequest {}
export interface GetDomainsResponse extends ApiSearchResponse<Domain> {}
export interface CreateDomainRequestData {
  domain: Domain
  user?: Pick<User, 'username' | 'emailAddress'> & {roleIds: number[]}
  sendPasswordEmail?: boolean
  theme?: string
}

export interface DomainListItem extends Domain {
  active: boolean
}

export class DisableDomainResultDTO {
  success: boolean
  activeSubDomains: DomainListItem[]
  errorMessage: string
}

@Injectable()
export class DomainService {
  constructor(
    private http: HttpClient,
    private logger: LoggerService,
  ) {}

  public getDomains(request?: GetDomainsRequest) {
    if (!request) {
      request = {}
    }
    const url = 'api://Domains'

    // convert all object properties to strings
    let params = new HttpParams()
    if (request.search) {
      params = params.set('search', request.search)
    }
    if (request.sort) {
      params = params
        .set('sort', request.sort)
        .set('asc', !request.asc ? 'false' : 'true')
    }
    if (request.offset) {
      params = params.set('offset', request.offset.toString())
    }
    if (request.results) {
      params = params.set('results', request.results.toString())
    }

    return this.http.get<GetDomainsResponse>(url, {params: params}).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getDomainsTree() {
    const url = 'api://Domains/Tree'
    return this.http.get<DomainSimple[]>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getDomain(code: string): Observable<Domain> {
    const url = `api://Domains/${code}`
    return this.http.get<Domain>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public postDomain(data: CreateDomainRequestData): Observable<Domain> {
    const url = 'api://Domains'
    // add empty templateIds array for backwards compatibility
    return this.http.post<Domain>(url, {...data, ...{templateIds: []}}).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public putDomain(code: string, domain: Domain): Observable<Domain> {
    const url = `api://Domains/${code}`

    return this.http.put<Domain>(url, domain).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public deleteDomain(code: string): Observable<void> {
    const url = `api://Domains/${code}`

    return this.http.delete<void>(url).pipe(
      tap(
        () => {
          this.logger.log('deleted domain')
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public putAttributes(code: string, attributes: DataMap): Observable<DataMap> {
    const url = `api://Domains/${code}/Attributes`

    return this.http.put<DataMap>(url, attributes).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public putData(code: string, data: DataMap): Observable<DataMap> {
    const url = `api://Domains/${code}/Data`

    console.log(data)

    return this.http.put<DataMap>(url, data).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getData(code: string): Observable<DataMap> {
    const url = `api://Domains/${code}/Data`

    return this.http.get<DataMap>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getLabels(code: string): Observable<string[]> {
    const url = `api://Domains/${code}/Labels'`

    return this.http.get<string[]>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public putContractTypes(
    code: string,
    contracttypes: ContractType[],
  ): Observable<ContractType[]> {
    const url = `api://Domains/${code}/ContractTypes`
    return this.http.put<ContractType[]>(url, contracttypes).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getContractTypes(code: string): Observable<ContractType[]> {
    const url = `api://Domains/${code}/ContractTypes`
    return this.http.get<ContractType[]>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public putTerminalTypes(
    code: string,
    terminaltypes: TerminalType[],
  ): Observable<TerminalType[]> {
    const url = `api://Domains/${code}/TerminalTypes`
    return this.http.put<TerminalType[]>(url, terminaltypes).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getTerminalTypes(code: string): Observable<TerminalType[]> {
    const url = `api://Domains/${code}/TerminalTypes`
    return this.http.get<TerminalType[]>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getAvailableTerminalTypes(code: string): Observable<TerminalType[]> {
    const url = `api://Domains/${code}/AvailableTerminalTypes`
    return this.http.get<TerminalType[]>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getAvailableTemplates(code: string): Observable<Template[]> {
    const url = `api://Domains/${code}/AvailableTemplates`
    return this.http.get<Template[]>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  public getAvailableAssets(code: string): Observable<Asset[]> {
    const url = `api://Domains/${code}/ConfigAssets`
    return this.http.get<Asset[]>(url).pipe(
      tap(
        (res) => {
          this.logger.log(res)
        },
        (err: Error) => {
          this.handleError(err)
        },
      ),
    )
  }

  private handleError(err: Error) {
    if (err instanceof HttpErrorResponse) {
      if (err.error && err.error.description) {
        throw new Error(err.error.description)
      } else {
        throw new Error('Error: ' + err.status + ' => ' + err.message)
      }
    }
  }

  public deactivateDomain(code: string): Observable<DisableDomainResultDTO> {
    const url = `api://Domains/${code}/disable`
    return this.http.post<DisableDomainResultDTO>(url, {}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
      map((res) => {
        if (!res.success && res?.activeSubDomains)
          res.activeSubDomains.forEach((domain) => (domain.active = true))
        return res
      }),
      catchError((err: HttpErrorResponse) => {
        // Handle the error here if needed
        this.logger.error(err)
        // Return the error as a response
        return of(err.error as DisableDomainResultDTO)
      }),
    )
  }

  public activateDomain(code: string) {
    // please dont.
    const url = `api://Domains/${code}/enable`
    return this.http.post<any>(url, {}).pipe(
      tap(
        (res) => {
          this.logger.log(res)
          return res
        },
        (err: Error) => {
          // this.handleError(err);
        },
      ),
    )
  }
}
